import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../api";

const inputCls =
  "w-full bg-[#12131f] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-[#25D366] transition-colors";

const emptyBrand = { name: "", logo: "", tint: "#f6c453", order: 0 };

const PERM_MSG = "You don't have permission for this. Please contact your admin to turn on this feature.";

const BrandForm = ({ open, onClose, onSave, initial }) => {
  const [form, setForm] = useState(initial ? { ...emptyBrand, ...initial } : emptyBrand);
  const [err, setErr] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setForm(initial ? { ...emptyBrand, ...initial } : emptyBrand);
    setErr("");
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    if (!file.type.startsWith("image/")) return setErr("Please select an image file");
    if (file.size > 1024 * 1024) return setErr("Image must be under 1MB");
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setErr("Brand name required");
    if (!form.logo) return setErr("Please upload a logo");
    onSave({ ...form, order: Number(form.order) || 0 });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1b1d2b] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{initial ? "Edit Brand" : "Add Brand"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          {/* Logo preview + upload */}
          <div className="flex items-center gap-4">
            <div className="w-28 h-16 shrink-0 rounded-lg bg-[#12131f] border border-white/10 flex items-center justify-center overflow-hidden">
              {form.logo ? (
                <img src={form.logo} alt="logo" className="max-h-12 max-w-[90%] object-contain" />
              ) : (
                <span className="text-white/30 text-xs">No logo</span>
              )}
            </div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-white bg-white/10 hover:bg-white/15 px-3 py-2 rounded-lg cursor-pointer transition-colors">
              <Upload className="w-4 h-4" /> Upload Logo
              <input type="file" accept="image/*" onChange={handleLogo} className="hidden" />
            </label>
          </div>
          <p className="text-white/40 text-[11px]">PNG/JPG, under 1MB. Transparent PNG works best on the dark cards.</p>

          <div>
            <label className="text-xs font-medium text-white/60 mb-1.5 block">Brand Name</label>
            <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Max66" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">Accent Colour</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.tint} onChange={(e) => set("tint", e.target.value)} className="w-10 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer p-0.5" />
                <input className={inputCls} value={form.tint} onChange={(e) => set("tint", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-white/60 mb-1.5 block">Order</label>
              <input type="number" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} />
            </div>
          </div>

          {err && <p className="text-red-400 text-sm">{err}</p>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg font-semibold text-sm bg-white/10 hover:bg-white/15 transition-colors">Cancel</button>
            <button type="submit" className="flex-1 py-2.5 rounded-lg font-semibold text-sm bg-[#25D366] hover:bg-[#1fb757] transition-colors">Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BrandsPanel = () => {
  const [brands, setBrands] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      setBrands(await api.listBrands());
    } catch (e) {
      console.error("Failed to load brands:", e);
      toast.error("Unable to load brands. Please try again.");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const handleSave = async (payload) => {
    try {
      if (editing) await api.updateBrand(editing.id, payload);
      else await api.createBrand(payload);
      await load();
      setFormOpen(false);
      toast.success(editing ? "Brand updated" : "Brand added");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? PERM_MSG : "Failed to save brand");
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteBrand(id);
      await load();
      toast.success("Brand removed");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? PERM_MSG : "Failed to remove brand");
    }
  };

  const move = async (id, direction) => {
    try {
      await api.moveBrand(id, direction);
      await load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? PERM_MSG : "Failed to reorder");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl text-white">Featured Brands</h1>
          <p className="text-white/50 text-sm mt-0.5">Logos shown on the landing page. Reorder with the arrows.</p>
        </div>
        <button onClick={() => { setEditing(null); setFormOpen(true); }} className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb757] text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">
          <Plus className="w-4 h-4" /> Add Brand
        </button>
      </div>

      <div className="grid gap-3">
        {brands.map((b, i) => (
          <div key={b.id} className="flex items-center justify-between gap-3 bg-[#1b1d2b] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="flex flex-col">
                <button disabled={i === 0} onClick={() => move(b.id, "up")} className="p-1 rounded hover:bg-white/10 disabled:opacity-30 transition-colors">
                  <ArrowUp className="w-4 h-4 text-white/70" />
                </button>
                <button disabled={i === brands.length - 1} onClick={() => move(b.id, "down")} className="p-1 rounded hover:bg-white/10 disabled:opacity-30 transition-colors">
                  <ArrowDown className="w-4 h-4 text-white/70" />
                </button>
              </div>
              <div className="w-28 h-14 shrink-0 rounded-lg bg-[#12131f] border border-white/10 flex items-center justify-center overflow-hidden" style={{ boxShadow: `inset 0 0 24px ${b.tint}22` }}>
                <img src={b.logo} alt={b.name} className="max-h-10 max-w-[85%] object-contain" />
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold text-white truncate">{b.name}</p>
                <span className="inline-flex items-center gap-1.5 text-xs text-white/50 mt-0.5">
                  <span className="w-3 h-3 rounded-full inline-block border border-white/20" style={{ background: b.tint }} /> {b.tint}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => { setEditing(b); setFormOpen(true); }} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <Pencil className="w-4 h-4 text-white/70" />
              </button>
              <button onClick={() => remove(b.id)} className="p-2 rounded-lg hover:bg-red-500/15 transition-colors">
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          </div>
        ))}
        {brands.length === 0 && <div className="text-center py-16 text-white/40">No brands yet. Add your first logo.</div>}
      </div>

      <BrandForm open={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave} initial={editing} />
    </div>
  );
};

export default BrandsPanel;
